import React, { useRef } from 'react';

import classNames from 'classnames';
import { useSelect } from 'downshift';

import Label from 'common/ui/common/Label';
import LabelWrapper from 'common/ui/common/select/LabelWrapper';
import OptionPrefix from 'common/ui/common/select/OptionPrefix';
import RightIcons from 'common/ui/common/select/RightIcons';
import SelectOptions from 'common/ui/common/select/SelectOptions';
import SelectPortal from 'common/ui/common/select/SelectPortal';
import SupportingTexts from 'common/ui/common/select/SupportingTexts';
import { sortByGroupBy } from 'common/ui/common/select/util';

import type { CommonSelectProps, Option } from 'common/ui/common/select/SelectCommon';

import 'css/components/_SelectCommon.scss';

export interface Props extends CommonSelectProps {
  onChange: (option?: Option) => void;
  value?: Option;
  showChevron?: boolean;
}

const SingleSelect = ({
  allowClear,
  className,
  cta,
  disabled,
  error,
  iconStrokeWidth = 2,
  isItemDisabled = () => false,
  label,
  labelPlacement = 'top',
  loading,
  onChange,
  options,
  optionsMaxWidth,
  optionsPlacement = 'bottom',
  placeholder,
  prefixIcon: PrefixIcon,
  required = false,
  supportingText,
  value,
  withBorder = true,
  showChevron = true,
  minWidth = 120,
}: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const sortedOptions = sortByGroupBy(options);

  const {
    closeMenu,
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    isItemDisabled,
    items: cta ? [...sortedOptions, cta.option] : sortedOptions,
    itemToString: (item) => (item ? String(item.label) : ''),
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      if (cta && newSelectedItem?.value === cta.option.value) {
        cta.onAction();
        return;
      }
      onChange(newSelectedItem ?? undefined);
    },
    selectedItem: value || null,
  });

  return (
    <div className={classNames('dropdownV2Wrapper', className)} ref={triggerRef}>
      <LabelWrapper labelPlacement={labelPlacement}>
        {label && <Label {...getLabelProps()} label={label} />}
        <div
          className={classNames('dropdownV2 singleSelect', {
            disabledV2: disabled,
            errorV2: error,
            withBorder,
          })}
          style={{ minWidth }}>
          <div className="dropdownWrapperV2">
            <div
              className="dropdownHeadWrapper"
              aria-required={required}
              aria-label="toggle menu"
              {...getToggleButtonProps({ disabled }, { suppressRefError: true })}>
              <div className="leftWrapper">
                {PrefixIcon && (
                  <PrefixIcon className="prefixIcon" size={14} strokeWidth={iconStrokeWidth} />
                )}
                {value ? (
                  <>
                    <OptionPrefix
                      flair={value.flair}
                      avatar={value.avatar}
                      prefixIcon={value.prefixIcon}
                      disabled={disabled}
                    />
                    <span className="selectedItem">{value.render ?? value.label}</span>
                  </>
                ) : (
                  <span className="dropdownPlaceholder">{placeholder}</span>
                )}
              </div>
              <RightIcons
                aria-label="remove selection"
                disabled={disabled}
                loading={loading}
                onClear={() => {
                  onChange();
                  closeMenu();
                }}
                showClear={allowClear && !!value}
                error={error}
                showChevron={showChevron}
              />
            </div>
            <SelectPortal
              getMenuProps={getMenuProps}
              optionsMaxWidth={optionsMaxWidth}
              position={optionsPlacement}
              triggerRef={triggerRef}>
              <SelectOptions
                ctaOption={cta?.option}
                disabled={disabled}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isItemDisabled={isItemDisabled}
                isOpen={isOpen}
                options={sortedOptions}
                optionsMaxWidth={optionsMaxWidth}
                optionsPlacement={optionsPlacement}
                selectedItems={value && [value]}
              />
            </SelectPortal>
          </div>
          <SupportingTexts supportingText={supportingText} error={!disabled ? error : undefined} />
        </div>
      </LabelWrapper>
    </div>
  );
};

export default SingleSelect;
